import React, { useState } from 'react'
import { Routes, Route, Link, useLocation } from 'react-router-dom'
import Layout from '../components/Layout'
import {
  ChartBarIcon,
  DocumentTextIcon,
  CogIcon,
  EyeIcon,
  CheckCircleIcon,
  ClockIcon,
  UserGroupIcon,
  MapPinIcon,
  CubeIcon,
  TruckIcon
} from '@heroicons/react/24/outline'
import RvmPlanningPage from './RvmPlanningPage'

type RegulatorTimeframe = 'week' | 'month' | 'quarter' | 'year'

const regulatorNavigationItems = [
  { name: 'Dashboard', href: '/regulators/dashboard', icon: ChartBarIcon },
  { name: 'Reports & Analytics', href: '/regulators/reports', icon: DocumentTextIcon },
  { name: 'Regulation Management', href: '/regulators/regulations', icon: CogIcon },
  { name: 'Audit Logs', href: '/regulators/audit-logs', icon: EyeIcon },
  { name: 'RVM Planning', href: '/regulators/planning', icon: MapPinIcon }
]

const regulatorTimeframeData: Record<RegulatorTimeframe, {
  stats: Array<{
    name: string
    value: string
    change: string
    changeType: 'increase' | 'decrease'
    icon: typeof ChartBarIcon
  }>
  recentTransactions: Array<{
    id: string
    timestamp: string
    rvm_id: string
    location: { district: string; address: string }
    items: Array<{ material_type: string; weight_grams: number; quantity: number; capacity_ml: number }>
    cash_redeemed_hkd: number
    payment_gateway: string
    status: string
  }>
  districtSummary: Array<{
    district: string
    bottles_collected: number
    weight_kg: number
    avg_daily: number
    cash_redeemed_hkd: number
  }>
  cashRedemptionSummary: Array<{
    gateway: string
    transactions: number
    total_hkd: number
    avg_per_transaction: number
  }>
}> = {
  week: {
    stats: [
      {
        name: 'Total PET Transactions',
        value: '46,080',
        change: '+612',
        changeType: 'increase',
        icon: ChartBarIcon
      },
      {
        name: 'Total Weight (kg)',
        value: '921.3',
        change: '+32.1',
        changeType: 'increase',
        icon: CheckCircleIcon
      },
      {
        name: 'Cash Redemptions (HKD)',
        value: 'HK$18,620',
        change: '+$1,480',
        changeType: 'increase',
        icon: ClockIcon
      },
      {
        name: 'Active RVMs',
        value: '594',
        change: '+3',
        changeType: 'increase',
        icon: UserGroupIcon
      }
    ],
    recentTransactions: [
      {
        id: '0x4a7e8c21...f1e2b45',
        timestamp: '2025-03-24T09:42:23+08:00',
        rvm_id: 'WATSNS_RVM_KT_001',
        location: { district: 'Kwun Tong', address: 'APM, 418 Kwun Tong Road' },
        items: [{ material_type: 'PET', weight_grams: 19.8, quantity: 2, capacity_ml: 600 }],
        cash_redeemed_hkd: 0.85,
        payment_gateway: 'Octopus',
        status: 'Cash Redeemed'
      },
      {
        id: '0x7b2f1a34...c8d9e12',
        timestamp: '2025-03-24T08:58:15+08:00',
        rvm_id: 'WATSNS_RVM_ST_002',
        location: { district: 'Sha Tin', address: 'New Town Plaza, Level 1' },
        items: [{ material_type: 'PET', weight_grams: 17.4, quantity: 1, capacity_ml: 500 }],
        cash_redeemed_hkd: 0.45,
        payment_gateway: 'AlipayHK',
        status: 'Cash Redeemed'
      },
      {
        id: '0x9c5e3f67...a4b7c90',
        timestamp: '2025-03-24T08:15:42+08:00',
        rvm_id: 'WATSNS_RVM_CW_003',
        location: { district: 'Causeway Bay', address: 'Times Square, B2' },
        items: [{ material_type: 'PET', weight_grams: 14.9, quantity: 2, capacity_ml: 330 }],
        cash_redeemed_hkd: 0.70,
        payment_gateway: 'WeChat Pay',
        status: 'Cash Redeemed'
      },
      {
        id: '0x2d8a1b56...f3e6d21',
        timestamp: '2025-03-24T07:53:18+08:00',
        rvm_id: 'WATSNS_RVM_MK_004',
        location: { district: 'Mong Kok', address: 'Langham Place, G/F' },
        items: [{ material_type: 'PET', weight_grams: 21.3, quantity: 3, capacity_ml: 600 }],
        cash_redeemed_hkd: 1.05,
        payment_gateway: 'PayMe',
        status: 'Cash Redeemed'
      }
    ],
    districtSummary: [
      { district: 'Sha Tin', bottles_collected: 5_640, weight_kg: 113, avg_daily: 805, cash_redeemed_hkd: 2_256 },
      { district: 'Eastern', bottles_collected: 4_950, weight_kg: 99, avg_daily: 707, cash_redeemed_hkd: 1_980 },
      { district: 'Yuen Long', bottles_collected: 4_820, weight_kg: 96, avg_daily: 689, cash_redeemed_hkd: 1_928 },
      { district: 'Kwun Tong', bottles_collected: 4_520, weight_kg: 90, avg_daily: 646, cash_redeemed_hkd: 1_808 },
      { district: 'Causeway Bay', bottles_collected: 4_120, weight_kg: 82, avg_daily: 589, cash_redeemed_hkd: 1_648 }
    ],
    cashRedemptionSummary: [
      { gateway: 'Octopus', transactions: 11_420, total_hkd: 4_568, avg_per_transaction: 0.40 },
      { gateway: 'AlipayHK', transactions: 9_820, total_hkd: 3_928, avg_per_transaction: 0.40 },
      { gateway: 'WeChat Pay', transactions: 7_460, total_hkd: 2_984, avg_per_transaction: 0.40 },
      { gateway: 'PayMe', transactions: 5_620, total_hkd: 2_248, avg_per_transaction: 0.40 }
    ]
  },
  month: {
    stats: [
      {
        name: 'Total PET Transactions',
        value: '184,250',
        change: '+2,345',
        changeType: 'increase',
        icon: ChartBarIcon
      },
      {
        name: 'Total Weight (kg)',
        value: '3,685',
        change: '+125.8',
        changeType: 'increase',
        icon: CheckCircleIcon
      },
      {
        name: 'Cash Redemptions (HKD)',
        value: 'HK$73,700',
        change: '+$5,900',
        changeType: 'increase',
        icon: ClockIcon
      },
      {
        name: 'Active RVMs',
        value: '598',
        change: '+12',
        changeType: 'increase',
        icon: UserGroupIcon
      }
    ],
    recentTransactions: [
      {
        id: '0x4a7e8c21...f1e2b45',
        timestamp: '2025-03-21T15:42:23+08:00',
        rvm_id: 'WATSNS_RVM_KT_001',
        location: { district: 'Kwun Tong', address: 'APM, 418 Kwun Tong Road' },
        items: [{ material_type: 'PET', weight_grams: 22.5, quantity: 2, capacity_ml: 600 }],
        cash_redeemed_hkd: 0.90,
        payment_gateway: 'Octopus',
        status: 'Cash Redeemed'
      },
      {
        id: '0x7b2f1a34...c8d9e12',
        timestamp: '2025-03-21T14:28:15+08:00',
        rvm_id: 'WATSNS_RVM_ST_002',
        location: { district: 'Sha Tin', address: 'New Town Plaza, Level 1' },
        items: [{ material_type: 'PET', weight_grams: 18.3, quantity: 1, capacity_ml: 500 }],
        cash_redeemed_hkd: 0.50,
        payment_gateway: 'AlipayHK',
        status: 'Cash Redeemed'
      },
      {
        id: '0x9c5e3f67...a4b7c90',
        timestamp: '2025-03-21T13:15:42+08:00',
        rvm_id: 'WATSNS_RVM_CW_003',
        location: { district: 'Causeway Bay', address: 'Times Square, B2' },
        items: [{ material_type: 'PET', weight_grams: 15.8, quantity: 3, capacity_ml: 330 }],
        cash_redeemed_hkd: 1.50,
        payment_gateway: 'WeChat Pay',
        status: 'Cash Redeemed'
      },
      {
        id: '0x2d8a1b56...f3e6d21',
        timestamp: '2025-03-21T12:33:18+08:00',
        rvm_id: 'WATSNS_RVM_MK_004',
        location: { district: 'Mong Kok', address: 'Langham Place, G/F' },
        items: [{ material_type: 'PET', weight_grams: 28.7, quantity: 4, capacity_ml: 600 }],
        cash_redeemed_hkd: 2.00,
        payment_gateway: 'PayMe',
        status: 'Cash Redeemed'
      }
    ],
    districtSummary: [
      { district: 'Sha Tin', bottles_collected: 22_500, weight_kg: 450, avg_daily: 3_214, cash_redeemed_hkd: 9_000 },
      { district: 'Eastern', bottles_collected: 19_800, weight_kg: 396, avg_daily: 2_828, cash_redeemed_hkd: 7_920 },
      { district: 'Yuen Long', bottles_collected: 19_250, weight_kg: 385, avg_daily: 2_750, cash_redeemed_hkd: 7_700 },
      { district: 'Kwun Tong', bottles_collected: 18_500, weight_kg: 370, avg_daily: 2_642, cash_redeemed_hkd: 7_400 },
      { district: 'Causeway Bay', bottles_collected: 17_200, weight_kg: 344, avg_daily: 2_457, cash_redeemed_hkd: 6_880 }
    ],
    cashRedemptionSummary: [
      { gateway: 'Octopus', transactions: 45_680, total_hkd: 18_272, avg_per_transaction: 0.40 },
      { gateway: 'AlipayHK', transactions: 38_920, total_hkd: 15_568, avg_per_transaction: 0.40 },
      { gateway: 'WeChat Pay', transactions: 29_540, total_hkd: 11_816, avg_per_transaction: 0.40 },
      { gateway: 'PayMe', transactions: 22_180, total_hkd: 8_872, avg_per_transaction: 0.40 }
    ]
  },
  quarter: {
    stats: [
      {
        name: 'Total PET Transactions',
        value: '552,750',
        change: '+7,035',
        changeType: 'increase',
        icon: ChartBarIcon
      },
      {
        name: 'Total Weight (kg)',
        value: '11,055',
        change: '+377.3',
        changeType: 'increase',
        icon: CheckCircleIcon
      },
      {
        name: 'Cash Redemptions (HKD)',
        value: 'HK$221,100',
        change: '+$18,500',
        changeType: 'increase',
        icon: ClockIcon
      },
      {
        name: 'Active RVMs',
        value: '612',
        change: '+24',
        changeType: 'increase',
        icon: UserGroupIcon
      }
    ],
    recentTransactions: [
      {
        id: '0x4a7e8c21...f1e2b45',
        timestamp: '2025-03-21T15:42:23+08:00',
        rvm_id: 'WATSNS_RVM_KT_001',
        location: { district: 'Kwun Tong', address: 'APM, 418 Kwun Tong Road' },
        items: [{ material_type: 'PET', weight_grams: 28.6, quantity: 3, capacity_ml: 600 }],
        cash_redeemed_hkd: 1.35,
        payment_gateway: 'Octopus',
        status: 'Cash Redeemed'
      },
      {
        id: '0x7b2f1a34...c8d9e12',
        timestamp: '2025-03-20T10:18:11+08:00',
        rvm_id: 'WATSNS_RVM_ST_002',
        location: { district: 'Sha Tin', address: 'New Town Plaza, Level 1' },
        items: [{ material_type: 'PET', weight_grams: 26.4, quantity: 2, capacity_ml: 750 }],
        cash_redeemed_hkd: 1.20,
        payment_gateway: 'AlipayHK',
        status: 'Cash Redeemed'
      },
      {
        id: '0x9c5e3f67...a4b7c90',
        timestamp: '2025-03-19T19:05:42+08:00',
        rvm_id: 'WATSNS_RVM_CW_003',
        location: { district: 'Causeway Bay', address: 'Times Square, B2' },
        items: [{ material_type: 'PET', weight_grams: 24.1, quantity: 4, capacity_ml: 500 }],
        cash_redeemed_hkd: 1.95,
        payment_gateway: 'WeChat Pay',
        status: 'Cash Redeemed'
      },
      {
        id: '0x2d8a1b56...f3e6d21',
        timestamp: '2025-03-18T08:43:18+08:00',
        rvm_id: 'WATSNS_RVM_MK_004',
        location: { district: 'Mong Kok', address: 'Langham Place, G/F' },
        items: [{ material_type: 'PET', weight_grams: 32.5, quantity: 5, capacity_ml: 750 }],
        cash_redeemed_hkd: 2.60,
        payment_gateway: 'PayMe',
        status: 'Cash Redeemed'
      }
    ],
    districtSummary: [
      { district: 'Sha Tin', bottles_collected: 67_500, weight_kg: 1_350, avg_daily: 3_750, cash_redeemed_hkd: 27_200 },
      { district: 'Eastern', bottles_collected: 59_400, weight_kg: 1_188, avg_daily: 3_300, cash_redeemed_hkd: 24_300 },
      { district: 'Yuen Long', bottles_collected: 57_750, weight_kg: 1_155, avg_daily: 3_200, cash_redeemed_hkd: 23_600 },
      { district: 'Kwun Tong', bottles_collected: 55_500, weight_kg: 1_110, avg_daily: 3_080, cash_redeemed_hkd: 22_800 },
      { district: 'Causeway Bay', bottles_collected: 51_600, weight_kg: 1_032, avg_daily: 2_860, cash_redeemed_hkd: 21_100 }
    ],
    cashRedemptionSummary: [
      { gateway: 'Octopus', transactions: 137_040, total_hkd: 54_816, avg_per_transaction: 0.40 },
      { gateway: 'AlipayHK', transactions: 116_760, total_hkd: 46_704, avg_per_transaction: 0.40 },
      { gateway: 'WeChat Pay', transactions: 88_620, total_hkd: 35_448, avg_per_transaction: 0.40 },
      { gateway: 'PayMe', transactions: 66_540, total_hkd: 26_616, avg_per_transaction: 0.40 }
    ]
  },
  year: {
    stats: [
      {
        name: 'Total PET Transactions',
        value: '2,193,000',
        change: '+28,140',
        changeType: 'increase',
        icon: ChartBarIcon
      },
      {
        name: 'Total Weight (kg)',
        value: '44,220',
        change: '+1,509.0',
        changeType: 'increase',
        icon: CheckCircleIcon
      },
      {
        name: 'Cash Redemptions (HKD)',
        value: 'HK$884,400',
        change: '+$74,000',
        changeType: 'increase',
        icon: ClockIcon
      },
      {
        name: 'Active RVMs',
        value: '650',
        change: '+62',
        changeType: 'increase',
        icon: UserGroupIcon
      }
    ],
    recentTransactions: [
      {
        id: '0x4a7e8c21...f1e2b45',
        timestamp: '2025-03-15T12:12:23+08:00',
        rvm_id: 'WATSNS_RVM_KT_001',
        location: { district: 'Kwun Tong', address: 'APM, 418 Kwun Tong Road' },
        items: [{ material_type: 'PET', weight_grams: 34.5, quantity: 4, capacity_ml: 750 }],
        cash_redeemed_hkd: 2.95,
        payment_gateway: 'Octopus',
        status: 'Cash Redeemed'
      },
      {
        id: '0x7b2f1a34...c8d9e12',
        timestamp: '2025-03-11T09:25:15+08:00',
        rvm_id: 'WATSNS_RVM_ST_002',
        location: { district: 'Sha Tin', address: 'New Town Plaza, Level 1' },
        items: [{ material_type: 'PET', weight_grams: 31.2, quantity: 3, capacity_ml: 1000 }],
        cash_redeemed_hkd: 2.40,
        payment_gateway: 'AlipayHK',
        status: 'Cash Redeemed'
      },
      {
        id: '0x9c5e3f67...a4b7c90',
        timestamp: '2025-03-08T17:51:42+08:00',
        rvm_id: 'WATSNS_RVM_CW_003',
        location: { district: 'Causeway Bay', address: 'Times Square, B2' },
        items: [{ material_type: 'PET', weight_grams: 29.7, quantity: 4, capacity_ml: 750 }],
        cash_redeemed_hkd: 2.85,
        payment_gateway: 'WeChat Pay',
        status: 'Cash Redeemed'
      },
      {
        id: '0x2d8a1b56...f3e6d21',
        timestamp: '2025-03-05T07:03:18+08:00',
        rvm_id: 'WATSNS_RVM_MK_004',
        location: { district: 'Mong Kok', address: 'Langham Place, G/F' },
        items: [{ material_type: 'PET', weight_grams: 38.9, quantity: 6, capacity_ml: 1000 }],
        cash_redeemed_hkd: 3.90,
        payment_gateway: 'PayMe',
        status: 'Cash Redeemed'
      }
    ],
    districtSummary: [
      { district: 'Sha Tin', bottles_collected: 270_000, weight_kg: 5_400, avg_daily: 3_700, cash_redeemed_hkd: 108_000 },
      { district: 'Eastern', bottles_collected: 237_600, weight_kg: 4_752, avg_daily: 3_259, cash_redeemed_hkd: 95_040 },
      { district: 'Yuen Long', bottles_collected: 231_000, weight_kg: 4_620, avg_daily: 3_162, cash_redeemed_hkd: 92_400 },
      { district: 'Kwun Tong', bottles_collected: 222_000, weight_kg: 4_440, avg_daily: 3_041, cash_redeemed_hkd: 88_800 },
      { district: 'Causeway Bay', bottles_collected: 206_400, weight_kg: 4_128, avg_daily: 2_828, cash_redeemed_hkd: 82_560 }
    ],
    cashRedemptionSummary: [
      { gateway: 'Octopus', transactions: 548_160, total_hkd: 219_264, avg_per_transaction: 0.40 },
      { gateway: 'AlipayHK', transactions: 467_040, total_hkd: 186_816, avg_per_transaction: 0.40 },
      { gateway: 'WeChat Pay', transactions: 354_480, total_hkd: 141_792, avg_per_transaction: 0.40 },
      { gateway: 'PayMe', transactions: 266_160, total_hkd: 106_464, avg_per_transaction: 0.40 }
    ]
  }
}

const RegulatorNavigation: React.FC = () => {
  const location = useLocation()

  return (
    <div className="border-b border-gray-200 mb-8">
      <nav className="flex flex-wrap gap-4 sm:gap-8">
        {regulatorNavigationItems.map((item) => {
          const isActive =
            location.pathname === item.href ||
            (item.href === '/regulators/dashboard' && location.pathname === '/regulators')

          return (
            <Link
              key={item.name}
              to={item.href}
              className={`flex items-center py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                isActive
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-primary-600 hover:border-primary-200'
              }`}
            >
              <item.icon className="h-5 w-5 mr-2" />
              {item.name}
            </Link>
          )
        })}
      </nav>
    </div>
  )
}

const RegulatorsPortal: React.FC = () => {
  return (
    <Layout portalType="regulators">
      <Routes>
        <Route path="/" element={<RegulatorsDashboard />} />
        <Route path="/dashboard" element={<RegulatorsDashboard />} />
        <Route path="/reports" element={<ReportsAnalytics />} />
        <Route path="/regulations" element={<RegulationManagement />} />
        <Route path="/audit-logs" element={<AuditLogs />} />
        <Route path="/planning" element={<RvmPlanningPage homeRoute="/regulators/dashboard" />} />
      </Routes>
    </Layout>
  )
}

const RegulatorsDashboard: React.FC = () => {
  const [selectedTimeframe, setSelectedTimeframe] = useState<RegulatorTimeframe>('month')

  const currentData = regulatorTimeframeData[selectedTimeframe]
  const statAccentClasses = [
    'bg-primary-100 text-primary-600',
    'bg-primary-200 text-primary-700',
    'bg-primary-100 text-primary-600',
    'bg-primary-200 text-primary-700'
  ]

  const formatCurrency = (value: number) =>
    `HK$${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'compliant':
        return 'text-green-600 bg-green-100'
      case 'pending':
        return 'text-yellow-600 bg-yellow-100'
      case 'non-compliant':
        return 'text-red-600 bg-red-100'
      case 'approved':
        return 'text-green-600 bg-green-100'
      case 'overdue':
        return 'text-red-600 bg-red-100'
      default:
        return 'text-gray-600 bg-gray-100'
    }
  }

  return (
    <div className="py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <RegulatorNavigation />

        {/* Page Header */}
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between mb-8 animate-fade-in">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Regulatory Overview</h1>
            <p className="text-gray-600">Monitor compliance and recycling performance for PET bottle collections</p>
          </div>
          <div className="flex items-center space-x-4">
            <select
              value={selectedTimeframe}
              onChange={(e) => setSelectedTimeframe(e.target.value as RegulatorTimeframe)}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="week">This Week</option>
              <option value="month">This Month</option>
              <option value="quarter">This Quarter</option>
              <option value="year">This Year</option>
            </select>
            <button className="bg-primary-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-primary-700 shadow-sm">
              Generate Report
            </button>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {currentData.stats.map((stat, index) => (
            <div key={`${selectedTimeframe}-${stat.name}`} className="bg-white rounded-lg shadow p-6 animate-fade-in">
              <div className="flex items-center">
                <div className={`p-2 rounded-lg ${statAccentClasses[index % statAccentClasses.length]}`}>
                  <stat.icon className="h-6 w-6" />
                </div>
                <div className="ml-4 flex-1">
                  <p className="text-sm font-medium text-gray-600">{stat.name}</p>
                  <div className="mt-1 flex items-baseline">
                    <p className="text-2xl font-semibold text-gray-900">{stat.value}</p>
                    <p className={`ml-2 text-sm font-medium ${
                      stat.changeType === 'increase' ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {stat.change}
                    </p>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Last updated: 8/10/2025</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Cash Redemption Summary */}
        <div className="bg-white rounded-lg shadow mb-8">
          <div className="p-6 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Cash Redemption Summary by Payment Gateway</h3>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {currentData.cashRedemptionSummary.map((gateway, index) => {
                const accentGradients = [
                  'from-primary-500 to-primary-600',
                  'from-primary-400 to-primary-600',
                  'from-primary-500 to-primary-700',
                  'from-primary-300 to-primary-500'
                ]

                return (
                  <div key={`${selectedTimeframe}-${gateway.gateway}`} className="text-center animate-fade-in">
                    <div className={`w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-r ${accentGradients[index % accentGradients.length]} flex items-center justify-center`}>
                    <CubeIcon className="h-8 w-8 text-white" />
                    </div>
                    <h4 className="font-medium text-gray-900 mb-1">{gateway.gateway}</h4>
                    <p className="text-2xl font-bold text-primary-600 mb-1">{gateway.transactions.toLocaleString()}</p>
                    <p className="text-sm text-gray-500">transactions</p>
                    <p className="text-sm text-primary-600 font-medium">{formatCurrency(gateway.total_hkd)}</p>
                    <p className="text-xs text-gray-500 mt-1">Avg HK${gateway.avg_per_transaction.toFixed(2)} per transaction</p>
                  </div>
                )
              })}
            </div>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* Recent Transactions */}
          <div className="bg-white rounded-lg shadow">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Recent Recycling Transactions</h3>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                {currentData.recentTransactions.map((tx) => (
                  <div key={`${selectedTimeframe}-${tx.id}`} className="border border-gray-200 rounded-lg p-4 animate-fade-in">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1">
                        <p className="text-sm font-mono text-gray-600">{tx.id}</p>
                        <p className="text-sm text-gray-500">{new Date(tx.timestamp).toLocaleString()}</p>
                      </div>
                      <div className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(tx.status.toLowerCase())}`}>
                        {tx.status}
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-gray-500">Location</p>
                        <p className="font-medium">{tx.location.district}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Material</p>
                        <p className="font-medium">{tx.items[0].material_type}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Weight</p>
                        <p className="font-medium">{tx.items[0].weight_grams}g</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Cash Redeemed</p>
                        <p className="font-medium text-primary-600">{formatCurrency(tx.cash_redeemed_hkd)}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Payment Gateway</p>
                        <p className="font-medium">{tx.payment_gateway}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* District Performance */}
          <div className="bg-white rounded-lg shadow">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Top Performing Districts</h3>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                {currentData.districtSummary.map((district, index) => (
                  <div key={`${selectedTimeframe}-${district.district}`} className="border border-gray-200 rounded-lg p-4 animate-fade-in">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-medium text-gray-900">{district.district}</h4>
                      <span className="text-sm text-gray-500">#{index + 1}</span>
                    </div>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-gray-500">Bottles Collected</p>
                        <p className="font-medium">{district.bottles_collected.toLocaleString()}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Total Weight</p>
                        <p className="font-medium">{district.weight_kg.toLocaleString()} kg</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Daily Average</p>
                        <p className="font-medium">{Math.round(district.avg_daily).toLocaleString()}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Cash Redeemed</p>
                        <p className="font-medium text-primary-600">{formatCurrency(district.cash_redeemed_hkd)}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

const ReportsAnalytics: React.FC = () => {
  const logisticsData = [
    {
      logistics_tx_id: "LOG_20250322_089001",
      timestamp: "2025-03-22T08:45:00+08:00",
      collection_point: "WATSNS_RVM_KT_001",
      weight_collected_kg: 125.75,
      logistics_provider: "GreenLogistics HK Ltd.",
      destination: "HK Recycling Plant, EcoPark, Tuen Mun",
      estimated_arrival: "2025-03-22T11:30:00+08:00",
      status: "In Transit"
    },
    {
      logistics_tx_id: "LOG_20250322_045678",
      timestamp: "2025-03-22T06:15:00+08:00",
      collection_point: "WATSNS_RVM_ST_002",
      weight_collected_kg: 89.25,
      logistics_provider: "EcoTrans Solutions",
      destination: "Central Sorting Facility, Tsing Yi",
      estimated_arrival: "2025-03-22T09:00:00+08:00",
      status: "Delivered"
    }
  ]

  return (
    <div className="py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <RegulatorNavigation />
        <h1 className="text-2xl font-bold text-gray-900 mb-8">Logistics Analytics</h1>
        
        {/* Logistics Tracking */}
        <div className="bg-white rounded-lg shadow mb-8">
          <div className="p-6 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900 flex items-center">
              <TruckIcon className="h-5 w-5 mr-2 text-primary-600" />
              Logistics Processing Records
            </h3>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              {logisticsData.map((logistics) => (
                <div key={logistics.logistics_tx_id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h4 className="font-medium text-gray-900">{logistics.logistics_tx_id}</h4>
                      <p className="text-sm text-gray-500">{new Date(logistics.timestamp).toLocaleString()}</p>
                    </div>
                    <span className={`px-3 py-1 text-xs font-medium rounded-full ${
                      logistics.status === 'Delivered' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {logistics.status}
                    </span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                    <div>
                      <p className="text-gray-500">Collection Point</p>
                      <p className="font-medium">{logistics.collection_point}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Weight Collected</p>
                      <p className="font-medium">{logistics.weight_collected_kg} kg</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Provider</p>
                      <p className="font-medium">{logistics.logistics_provider}</p>
                    </div>
                  </div>
                  <div className="mt-3 pt-3 border-t border-gray-100">
                    <p className="text-sm text-gray-500">Destination: {logistics.destination}</p>
                    <p className="text-sm text-gray-500">ETA: {new Date(logistics.estimated_arrival).toLocaleString()}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

const RegulationManagement: React.FC = () => {
  const weeklyReport = {
    report_id: "WEEKLY_SUMMARY_2025_W12",
    time_period: "2025-03-17 to 2025-03-23",
    total_collected: {
      bottles: 184250,
      weight_kg: 3685
    },
    top_performing_districts: [
      { district: "Sha Tin", bottles_collected: 22500, avg_daily_collection: 3214 },
      { district: "Eastern", bottles_collected: 19800, avg_daily_collection: 2828 },
      { district: "Yuen Long", bottles_collected: 19250, avg_daily_collection: 2750 }
    ],
    material_composition: {
      PET: "68%",
      HDPE: "22%",
      PP: "8%",
      Other: "2%"
    }
  }

  return (
    <div className="py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <RegulatorNavigation />
        <h1 className="text-2xl font-bold text-gray-900 mb-8">Weekly Regional Summary</h1>
        
        {/* Report Header */}
        <div className="bg-white rounded-lg shadow mb-8">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-medium text-gray-900">{weeklyReport.report_id}</h3>
                <p className="text-sm text-gray-500">Period: {weeklyReport.time_period}</p>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold text-primary-600">{weeklyReport.total_collected.bottles.toLocaleString()}</p>
                <p className="text-sm text-gray-500">Total Bottles</p>
              </div>
            </div>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-primary-50 rounded-lg p-4">
                <h4 className="font-medium text-primary-900 mb-2">Total Weight Collected</h4>
                <p className="text-3xl font-bold text-primary-600">{weeklyReport.total_collected.weight_kg} kg</p>
              </div>
              <div className="bg-primary-50 rounded-lg p-4">
                <h4 className="font-medium text-primary-900 mb-2">Estimated Credits Generated</h4>
                <p className="text-3xl font-bold text-primary-600">{(weeklyReport.total_collected.weight_kg * 0.5).toFixed(1)}</p>
              </div>
            </div>
          </div>
        </div>

        {/* District Performance */}
        <div className="bg-white rounded-lg shadow mb-8">
          <div className="p-6 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900 flex items-center">
              <MapPinIcon className="h-5 w-5 mr-2 text-primary-600" />
              Top Performing Districts
            </h3>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              {weeklyReport.top_performing_districts.map((district, index) => (
                <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center">
                    <div className="w-8 h-8 bg-primary-600 text-white rounded-full flex items-center justify-center text-sm font-bold mr-4">
                      {index + 1}
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-900">{district.district}</h4>
                      <p className="text-sm text-gray-500">Daily Average: {district.avg_daily_collection}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-bold text-primary-600">{district.bottles_collected.toLocaleString()}</p>
                    <p className="text-sm text-gray-500">bottles collected</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Material Composition */}
        <div className="bg-white rounded-lg shadow">
          <div className="p-6 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Material Composition</h3>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {Object.entries(weeklyReport.material_composition).map(([material, percentage]) => (
                <div key={material} className="text-center p-4 bg-gray-50 rounded-lg">
                  <div className="w-12 h-12 mx-auto mb-2 rounded-full bg-gradient-to-r from-primary-500 to-primary-500 flex items-center justify-center">
                    <span className="text-white font-bold text-sm">{material}</span>
                  </div>
                  <p className="text-2xl font-bold text-gray-900">{percentage}</p>
                  <p className="text-sm text-gray-500">{material}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

const AuditLogs: React.FC = () => {
  const [selectedTx, setSelectedTx] = useState<string | null>(null)

  const auditTrail = [
    {
      blockchain_tx_id: "0x4a7e8c21b9d12f45a72c1b8e5f6d934c8a1b0e3d7c9f2a4e5b6d8c3a0f1e2b45",
      timestamp: "2025-03-21T15:42:23+08:00",
      rvm_id: "WATSNS_RVM_KT_001",
      location: {
        district: "Kwun Tong",
        address: "APM, 418 Kwun Tong Road"
      },
      items: [{
        material_type: "PET",
        weight_grams: 22.5,
        quantity: 2,
        capacity_ml: 600,
        barcode_identified: true
      }],
      user: {
        hashed_id: "f7d82b9c4a6e1f0d3c5b2a8e9f1d4c7a"
      },
      plastic_credits_issued: 5.2,
      status: "Collected, pending transportation",
      logistics_linked: "LOG_20250322_089001",
      credit_tx_linked: "CREDIT_TX_20250321_158322"
    },
    {
      blockchain_tx_id: "0x7b2f1a34c8d9e12f45a72c1b8e5f6d934c8a1b0e3d7c9f2a4e5b6d8c3a0f1e2c89",
      timestamp: "2025-03-21T14:28:15+08:00",
      rvm_id: "WATSNS_RVM_ST_002",
      location: {
        district: "Sha Tin",
        address: "New Town Plaza, Level 1"
      },
      items: [{
        material_type: "HDPE",
        weight_grams: 18.3,
        quantity: 1,
        capacity_ml: 500,
        barcode_identified: true
      }],
      user: {
        hashed_id: "a1b2c3d4e5f6g7h8i9j0k1l2m3n4o5p6"
      },
      plastic_credits_issued: 4.1,
      status: "Processing complete",
      logistics_linked: "LOG_20250322_045678",
      credit_tx_linked: "CREDIT_TX_20250321_142156"
    }
  ]

  return (
    <div className="py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <RegulatorNavigation />
        <h1 className="text-2xl font-bold text-gray-900 mb-8">Blockchain Audit Trail</h1>
        
        <div className="space-y-6">
          {auditTrail.map((transaction) => (
            <div key={transaction.blockchain_tx_id} className="bg-white rounded-lg shadow">
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-medium text-gray-900">Blockchain Transaction</h3>
                    <p className="text-sm font-mono text-gray-500">{transaction.blockchain_tx_id}</p>
                  </div>
                  <button
                    onClick={() => setSelectedTx(selectedTx === transaction.blockchain_tx_id ? null : transaction.blockchain_tx_id)}
                    className="px-4 py-2 text-sm font-medium text-primary-600 bg-primary-50 rounded-md hover:bg-primary-100"
                  >
                    {selectedTx === transaction.blockchain_tx_id ? 'Hide Details' : 'View Details'}
                  </button>
                </div>
              </div>
              
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">Transaction Info</h4>
                    <div className="space-y-1 text-sm">
                      <p><span className="text-gray-500">Timestamp:</span> {new Date(transaction.timestamp).toLocaleString()}</p>
                      <p><span className="text-gray-500">RVM ID:</span> {transaction.rvm_id}</p>
                      <p><span className="text-gray-500">Status:</span> <span className="text-primary-600">{transaction.status}</span></p>
                    </div>
                  </div>
                  
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">Location</h4>
                    <div className="space-y-1 text-sm">
                      <p><span className="text-gray-500">District:</span> {transaction.location.district}</p>
                      <p><span className="text-gray-500">Address:</span> {transaction.location.address}</p>
                    </div>
                  </div>
                  
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">Credits & Material</h4>
                    <div className="space-y-1 text-sm">
                      <p><span className="text-gray-500">Material:</span> {transaction.items[0].material_type}</p>
                      <p><span className="text-gray-500">Weight:</span> {transaction.items[0].weight_grams}g</p>
                      <p><span className="text-gray-500">Credits:</span> <span className="text-green-600 font-medium">{transaction.plastic_credits_issued}</span></p>
                    </div>
                  </div>
                </div>

                {selectedTx === transaction.blockchain_tx_id && (
                  <div className="border-t border-gray-200 pt-6">
                    <h4 className="font-medium text-gray-900 mb-4">Complete Transaction Details</h4>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <pre className="text-xs text-gray-700 overflow-x-auto">
                        {JSON.stringify(transaction, null, 2)}
                      </pre>
                    </div>
                    
                    <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="bg-primary-50 rounded-lg p-4">
                        <h5 className="font-medium text-primary-900 mb-2">Linked Logistics Record</h5>
                        <p className="text-sm text-primary-700">{transaction.logistics_linked}</p>
                      </div>
                      <div className="bg-primary-50 rounded-lg p-4">
                        <h5 className="font-medium text-primary-900 mb-2">Linked Credit Transaction</h5>
                        <p className="text-sm text-primary-700">{transaction.credit_tx_linked}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

export default RegulatorsPortal
